import { useContext, useEffect } from 'react'
import { TableContext } from 'Context/Table'
import { UserModalContext } from '../FormUserModal/Context'
import { UIContext } from 'Context/UI'
import { useNavigate } from 'react-router-dom'
import { TABS } from '../Config'

import DoubleLineImage from 'Presentation/Components/Table/Cells/DoubleLineImage'
import CustomActionCell from 'Presentation/Components/Table/Components/CustomActionCell'

import { useUserStore } from 'Store/User'
import { useProfileStore } from 'Store/Profile'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import { generateImageUrl } from 'Utils/generateImageUrl'
import Swal from 'sweetalert2'

const VIEW_INDEX = 5

const ViewModel = () => {
	const { setShowContentToolbar, setCustomContentToolbar } = useContext(UIContext)
	const { setOpen, setFormType } = useContext(UserModalContext)
	const profile = useProfileStore(state => state.profile, shallow)
	const {
		updateLoading,
		updateError,
		updatePagination,
		updateSorting,
		resetState: clearStateTable,
	} = useContext(TableContext)
	const { data, getAll } = useUserStore(
		state => ({
			data: state.data,
			getAll: state.getAll,
		}),
		shallow
	)
	const setSelectedId = useUserStore(state => state.setSelectedId, shallow)
	const deleteSupplierUser = useUserStore(state => state.delete, shallow)
	const navigate = useNavigate()

	const findTabPath = (tabIndex: number) => {
		const tab = TABS.find((_, idx) => idx === tabIndex)
		if (tab) {
			return tab.path
		}
	}

	const onChangeTab = (tabIndex: number) => {
		if (tabIndex !== VIEW_INDEX) {
			navigate(findTabPath(tabIndex) ?? '#')
		}
	}

	const fetchData = async () => {
		updateLoading(true)
		const { success, data } = await getAll()
		if (!success) {
			updateError(true)
			toast.error(data)
			return
		}
		updateLoading(false)
	}

	const setupTable = () => {
		updateError(false)
		updatePagination(true)
		updateSorting('name', true)
	}

	const createUser = () => {
		setFormType('add')
		setOpen(true)
	}

	const editUser = (userId: string) => {
		setSelectedId(userId)
		setFormType('edit')
		setOpen(true)
	}

	const deleteUser = async (userId: number) => {
		const { success, data } = await deleteSupplierUser(userId)
		if (!success) {
			toast.error(data)
			return
		}
		toast.success('Delete user successfully')
		fetchData()
	}

	const askDeleteSupplierUser = async (userId: number) => {
		Swal.fire({
			title: 'Are you sure?',
			text: 'You will not be able to recover this user!',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Confirm',
			cancelButtonText: 'Cancel',
			customClass: {
				confirmButton: 'btn btn-kumopack-primary-400 text-kumopack-base-white',
				cancelButton: 'btn btn-active-light',
			},
			buttonsStyling: false,
		}).then(async result => {
			if (result.isConfirmed) {
				await deleteUser(userId)
			}
		})
	}

	const COLUMN_CONFIGS: any[] = [
		{
			Header: 'Name',
			accessor: 'name',
			minWidth: 150,
			width: 150,
			Cell: ({ row }: { row: any }) => (
				<DoubleLineImage
					img={
						row.original.pictureProfilePath
							? generateImageUrl(row.original.pictureProfilePath)
							: null
					}
					label={row.original.fullnameTh}
					description={row.original.fullnameEn}
				/>
			),
		},
		{
			Header: 'Email',
			accessor: 'email',
			minWidth: 100,
			width: 100,
			Cell: ({ value }: { value: any }) => <div className="text-kumopack-grey-600">{value}</div>,
		},
		{
			Header: 'Role',
			accessor: 'roleTitle',
			minWidth: 120,
			width: 120,
			Cell: ({ value }: { value: any }) => <div className="text-kumopack-grey-600">{value}</div>,
		},
		{
			Header: '',
			accessor: 'action',
			width: 50,
			minWidth: 50,
			Cell: ({ row }: any) => (
				<div className="text-center">
					<CustomActionCell
						hideView
						id={row.original.id}
						onClickEdit={editUser}
						onClickDelete={askDeleteSupplierUser}
					/>
				</div>
			),
			disableSortBy: true,
		},
	]

	// Setup Table
	useEffect(() => {
		setupTable()
		fetchData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Unmount Handler
	useEffect(() => {
		return () => {
			clearStateTable()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		setShowContentToolbar(false)
		setCustomContentToolbar(null)

		return () => {
			setShowContentToolbar(true)
			setCustomContentToolbar(null)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {
		companyName: profile?.companyName,
		currentTab: VIEW_INDEX,
		onChangeTab,
		data,
		COLUMN_CONFIGS,
		createUser,
	}
}

export default ViewModel
